import { Card, FormLabel, GridLegacy, List, ListItem, Stack, TextField, Typography } from "@mui/material";
import { Delete, Save } from "@mui/icons-material";
import Box from "@mui/material/Box";
import React, { useEffect, useRef } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { v4 } from "uuid";
import Button from "../../../components/CustomButtons/Button";
import CardBody from "../../../components/Card/CardBody";
import FormFile from "../../../components/Forms/FormFile";
import FormCustomizableTextTC from "../../../components/Forms/FormCustomizableTextTC";
import { ImageResolutionHint } from "../../../components/ImageHints/ImageResolutionHint";
import TranslationWrapper from "../../../components/TranslationWrapper/TranslationWrapper";
import usePickMenu, { NO_MENU_SELECTED } from "../../Upsell/modernTS/JUI/Menus/usePickMenu";
import {
  CategoryItemPivot,
  useCreateCategoryItemMutation,
  useDeleteCategoryItemMutation,
  useUpdateCategoryItemMutation,
} from "../../../store/api/campaignCategoryApi";
import _ from "lodash";

interface CategoryFormData {
  category_item_pivots: CategoryItemPivot[];
}

export default function ViewInCategory() {
  const campaignId: number = useWatch({ name: "id" });
  const salesareaId: number = useWatch({ name: "vestiging_id" });
  const categoryItems = useWatch({ name: "category_items" });

  const toDeletes = useRef<Record<number, true>>({});
  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    formState: { isDirty },
  } = useForm<CategoryFormData>();
  const { fields, append, prepend, remove, swap, move, insert, replace } = useFieldArray({
    control,
    name: "category_item_pivots",
    keyName: "key",
  });

  const [createCategoryItems] = useCreateCategoryItemMutation();
  const [updateCategoryItems] = useUpdateCategoryItemMutation();
  const [deleteCategoryItem] = useDeleteCategoryItemMutation();

  const openCategorySelector = usePickMenu({
    menuSalesareaId: salesareaId,
    allowMultiple: true,
  });

  // Initialize form data
  useEffect(() => {
    if (!categoryItems || isDirty) return;

    const formattedItems: CategoryItemPivot[] = categoryItems.map((item) => ({
      ...item.pivot,
      category_name: item.naam,
    }));

    reset({ category_item_pivots: formattedItems });
  }, [categoryItems]);

  const handleSave = async (data: CategoryFormData) => {
    const isExistingItem = (item: CategoryItemPivot) =>
      item?.id && !String(item.id).includes("-") && !isNaN(Number(item.id));

    const create = data.category_item_pivots.filter((item) => !isExistingItem(item));
    const update = data.category_item_pivots.filter(isExistingItem);
    const deletes = _.keys(toDeletes.current);

    try {
      const [createResult, updateResult] = await Promise.all([
        create.length > 0 ? createCategoryItems(create) : null,
        update.length > 0 ? updateCategoryItems(update) : null,
        ..._.map(deletes, (pivotId) => deleteCategoryItem(Number(pivotId))),
      ]);

      toDeletes.current = [];

      if (createResult != null && createResult?.data?.status !== "ok") {
        toast.error("Error: No items added");
        return;
      }

      if (updateResult != null && updateResult?.data?.status !== "ok") {
        toast.error("Error: No items updated");
        return;
      }

      const newItems = createResult?.data.data ?? [];

      const updatedItems = [...update, ...newItems];
      reset({ category_item_pivots: updatedItems });
      toast.success("All items saved successfully");
    } catch (error) {
      toast.error("An error occurred while saving items");
      console.error("Save error:", error);
    }
  };

  const handleAddCategories = async () => {
    const selectedCategories = await openCategorySelector();
    if (selectedCategories === NO_MENU_SELECTED) return;

    const currentItems = watch("category_item_pivots") || [];
    const existingCategoryIds = new Set(currentItems.map((item) => item.category_id));

    const newCategories = selectedCategories
      .filter((category) => !existingCategoryIds.has(category.id))
      .map((category) => ({
        id: v4(),
        category_id: category.id,
        category_name: category.naam,
        category_item_type: "campaign",
        category_item_id: campaignId,
        sort_key: 50,
      }));

    newCategories.forEach((newCategory) => {
      delete toDeletes.current[newCategory.id];
    });

    if (newCategories.length > 0) {
      insert(currentItems.length, newCategories);
    } else {
      toast.warn("Selected categories are already present in this campaign.");
    }
  };

  const handleRemove = async (index: number) => {
    if (!Number.isNaN(Number(index))) {
      toDeletes.current[watch("category_item_pivots")[index].id] = true;
    }
    remove(index);
  };

  return (
    <>
      <Card sx={{ p: 2 }}>
        <Stack direction={"column"} gap={2}>
          <Stack alignItems={"center"} direction={"row"} gap={6}>
            <Box>
              <FormLabel>
                <TranslationWrapper tDefault={"Image"} tKey={"campaigns.view_in_category.media_in_category"} />
              </FormLabel>
              <ImageResolutionHint res={"300x300"} />
            </Box>
            <FormFile canCrop={true} name={"media_in_category"} nullable={true} type={"image"} />
          </Stack>
          {campaignId > 0 ? (
            <FormCustomizableTextTC
              model={"campaign"}
              modelId={campaignId}
              tDefault={"Title"}
              tKey={"campaigns.view_in_category.title"}
              translatableText={"view_in_category_title"}
            />
          ) : null}
        </Stack>
      </Card>
      <Card sx={{ marginTop: 1 }}>
        <CardBody style={{ padding: 16 }}>
          <Stack alignItems={"center"} direction={"row"} justifyContent={"space-between"} sx={{ mb: 2 }}>
            <Button color={"primary"} onClick={handleAddCategories}>
              Add Category
            </Button>
            <Button
              color={"success"}
              disabled={!isDirty}
              onClick={handleSubmit((data) => handleSave(data))}
              startIcon={<Save />}
            >
              Save All Changes
            </Button>
          </Stack>
          <GridLegacy container sx={{ gap: 1 }}>
            <GridLegacy item xs={12}>
              <List>
                <ListItem>
                  <GridLegacy container>
                    <GridLegacy item xs={2}>
                      <TranslationWrapper tKey={"Category id"} />
                    </GridLegacy>
                    <GridLegacy item xs={3}>
                      <TranslationWrapper tKey={"Category name"} />
                    </GridLegacy>
                    <GridLegacy item sx={{ textAlign: "center" }} xs={3}>
                      <TranslationWrapper tKey={"Sort key"} />
                    </GridLegacy>
                    <GridLegacy item sx={{ textAlign: "center" }} xs={2}>
                      <TranslationWrapper tKey={"Delete"} />
                    </GridLegacy>
                  </GridLegacy>
                </ListItem>
                {fields?.map((item, index) => (
                  <ListItem key={`${item.id}`}>
                    <GridLegacy container sx={{ alignItems: "center" }}>
                      <GridLegacy item xs={2}>
                        <Typography>{item.category_id}</Typography>
                      </GridLegacy>
                      <GridLegacy item xs={3}>
                        <Typography>{item.category_name}</Typography>
                      </GridLegacy>
                      <GridLegacy item xs={3}>
                        <TextField
                          {...register(`category_item_pivots.${index}.sort_key`, {
                            required: true,
                            value: item.sort_key,
                            valueAsNumber: true,
                          })}
                          size={"small"}
                          variant={"outlined"}
                        />
                      </GridLegacy>
                      <GridLegacy item sx={{ textAlign: "center" }} xs={2}>
                        <Button
                          color={"error"}
                          onClick={() => handleRemove(index)}
                          sx={{ width: "40px", height: "40px" }}
                        >
                          <Delete />
                        </Button>
                      </GridLegacy>
                    </GridLegacy>
                  </ListItem>
                ))}
              </List>
            </GridLegacy>
          </GridLegacy>
        </CardBody>
      </Card>
    </>
  );
}
